import { Component, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap'; // quita NgbTypeahead
import { Observable, OperatorFunction, Subject, TimeoutError, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, timeout } from 'rxjs/operators'; // quita filter
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProspectsService } from 'src/app/services/prospects/prospects.service';
import { UserService } from 'src/app/services/user/user.service';
import { CampaignsService } from 'src/app/services/campaigns/campaigns.service';
import { getFirebaseBackend } from 'src/app/authUtils';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

interface SearchOption {
	value: string;
	label: string;
	placeholder: string;
	helper: string;
	inputType: string;
	minLength?: number;
}

@Component({
	selector: 'app-search',
	templateUrl: './search.component.html',
	styleUrls: ['./search.component.scss'],
})
export class SearchComponent implements OnInit {
	public searchForm: FormGroup;
	result: any[] = [];
	user: any = null;
	selectedUserPhone: string = '';

	public isSearching = false;

	public campaigns: any[] = [];
	public selectedCampaign: any = '';

	public isLoadingCampaigns = false;
	public isValidatingCampaign = false;

	public campaignSearchTerm = '';

	private campaignFocus$ = new Subject<string>();
	private campaignClick$ = new Subject<string>();

	public searchOptions: SearchOption[] = [
		{ value: 'phone', label: 'Phone', placeholder: 'Enter phone number...', helper: 'Include the area code.', inputType: 'text' },
		{ value: 'lead_id', label: 'Lead ID', placeholder: 'Enter Lead ID...', helper: '', inputType: 'text' },
		{ value: 'last_name', label: 'Last Name', placeholder: 'Enter last name...', helper: 'Enter at least 3 characters — very short/common last names can match too many records and time out.', inputType: 'text', minLength: 3 },
		{ value: 'email', label: 'Email', placeholder: 'Enter email address...', helper: '', inputType: 'email' },
		{ value: 'company_name', label: 'Company Name', placeholder: 'Enter company name...', helper: 'Enter at least 3 characters — very short/common names can match too many records and time out.', inputType: 'text', minLength: 3 },
	];

	private static readonly SEARCH_TIMEOUT_MS = 15000;

	constructor(
		private fb: FormBuilder,
		private userSrv: UserService,
		private prospectsSrv: ProspectsService,
		private modalService: NgbModal,
		private campaignsSrv: CampaignsService,
		private router: Router,
	) {
		this.user = getFirebaseBackend()?.getAuthenticatedUser();
	}

	ngOnInit() {
		this.userSrv.saveAction('Ingreso a Search');
		this.initForm();
		this.setupDynamicValidators();
		this.getAssignedCampaigns();
	}

	get selectedOption(): SearchOption | undefined {
		return this.searchOptions.find((o) => o.value === this.searchForm?.get('searchType')?.value);
	}

	private initForm() {
		this.searchForm = this.fb.group({
			searchType: ['phone', Validators.required],
			searchValue: ['', [Validators.required, Validators.pattern(/^[0-9()+\-\s]*$/)]],
		});
	}

	private setupDynamicValidators() {
		this.searchForm.get('searchType').valueChanges.subscribe((type: string) => {
			const valueControl = this.searchForm.get('searchValue');
			valueControl.setValue('');

			const option = this.searchOptions.find((o) => o.value === type);
			const validators = [Validators.required];
			if (type === 'email') {
				validators.push(Validators.email);
			}
			if (type === 'phone') {
				validators.push(Validators.pattern(/^[0-9()+\-\s]*$/));
			}
			if (option?.minLength) {
				validators.push(Validators.minLength(option.minLength));
			}

			valueControl.setValidators(validators);
			valueControl.updateValueAndValidity({ emitEvent: false });
		});
	}

	public onSubmit() {
		if (this.isSearching || this.searchForm.invalid) {
			return;
		}

		const { searchType, searchValue } = this.searchForm.value;
		const payload = { [searchType]: searchValue };
		this.isSearching = true;

		this.prospectsSrv
			.searchProspects(payload)
			.pipe(timeout(SearchComponent.SEARCH_TIMEOUT_MS))
			.subscribe(
				(response: any) => {
					this.result = response.body || [];
					this.isSearching = false;

					if (this.result.length === 0) {
						Swal.fire({
							icon: 'info',
							title: 'No results found',
							text: 'No prospects were found matching your search criteria.',
						});
					}
				},
				(error) => {
					console.error('Search error:', error);
					this.isSearching = false;
					const isTimeout = error instanceof TimeoutError || error?.status === 504 || error?.status === 0;

					if (isTimeout) {
						Swal.fire({
							icon: 'warning',
							title: 'Search is taking too long',
							text: 'Your search matched too many records to complete in time. Try a more specific term, or search by phone, lead ID, or email instead.',
						});
					} else {
						Swal.fire({
							icon: 'error',
							title: 'Search error',
							text: 'An error occurred while searching. Please try again.',
						});
					}
				},
			);
	}

	getAssignedCampaigns() {
		this.isLoadingCampaigns = true;
		return this.campaignsSrv.getCallerCampaigns(this.user.IdUser).subscribe({
			next: (res: any) => {
				this.campaigns = res.body;
				this.isLoadingCampaigns = false;
			},
			error: (err) => {
				console.error('Error fetching campaigns:', err);
				this.isLoadingCampaigns = false;
			},
		});
	}

	public openCampaignModal(content: any, prospectPhone: string) {
		this.selectedCampaign = '';
		this.campaignSearchTerm = '';
		this.selectedUserPhone = prospectPhone;
		this.modalService.open(content, { centered: true, windowClass: 'modal-md' });
	}

	searchCampaigns: OperatorFunction<string, readonly any[]> = (text$: Observable<string>) => {
		const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());

		return merge(debouncedText$, this.campaignFocus$, this.campaignClick$).pipe(map((term) => (!term ? this.campaigns : this.campaigns.filter((c) => c.name?.toLowerCase().includes(term.toLowerCase())))));
	};
	public onCampaignInputFocus(value: string) {
		this.campaignFocus$.next(value);
	}

	public onCampaignInputClick(value: string) {
		this.campaignClick$.next(value);
	}
	campaignFormatter = (campaign: any) => campaign?.name ?? '';

	public onCampaignSelected(event: NgbTypeaheadSelectItemEvent) {
		this.selectedCampaign = event.item;
	}

	public confirmOpen(modalRef: any) {
		modalRef.close();
	}

	openProspect() {
		if (!this.selectedCampaign || this.isValidatingCampaign) {
			return;
		}

		this.isValidatingCampaign = true;

		this.campaignsSrv.getCallerCampaigns(this.user.IdUser).subscribe({
			next: (res: any) => {
				this.isValidatingCampaign = false;
				const freshCampaigns = res.body || [];
				this.campaigns = freshCampaigns;

				const stillValid = freshCampaigns.find((c: any) => c.id === this.selectedCampaign.id);

				if (!stillValid) {
					this.selectedCampaign = '';
					this.campaignSearchTerm = '';
					Swal.fire('⚠️', "This campaign doesn't need anymore leads", 'warning');
					return;
				}
				this.prospectsSrv.currentCampaignName = stillValid.name;

				this.modalService.dismissAll();
				this.router.navigateByUrl(`/prospect-info/${stillValid.id}/${stillValid.alias}/${this.selectedUserPhone}?from=search`);
			},
			error: (err) => {
				this.isValidatingCampaign = false;
				console.error('Error validating campaign:', err);
				Swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Could not verify the campaign. Please try again.',
				});
			},
		});
	}
}
